import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import Head from "../../../layout/head/Head";
import ContentAlt from "../../../layout/content/ContentAlt";
import { IgChatContext, IgChatContextProvider } from "./IgChatContext";
import IgChatAside from "./IgChatAside";
import IgChatBody from "./IgChatBody";
import { useIgWebSocket } from "../../../services/igWebSocket";
import { igGetChatById, igMarkChatRead } from "../../../services/igAdapter";

const InstagramInner = () => {
  const { selectedChatState, chatListState, selectedIdState } = useContext(IgChatContext);
  const [selectedChat, setSelectedChat] = selectedChatState;
  const [chatList, setChatList] = chatListState;
  const [, setSelectedId] = selectedIdState;

  const [cookies] = useCookies(["id", "departmentIds"]);
  const roleFunctions = JSON.parse(localStorage.getItem("permission") || "{}")?.chats?.access || [];
  const canSeeAll = roleFunctions.includes("Access_All_Dept");
  const onlyOwn = roleFunctions.includes("Access_Own") && !canSeeAll; // SALES — hanya chat milik sendiri
  const currentUserId = cookies.id;

  // userDeptIdsRef selalu up-to-date (tidak stale) — di-update setiap render.
  // Dipakai di dalam useCallback dengan empty deps agar tidak recreate semua handlers.
  // Handle cookie sebagai array, single string, atau CSV string.
  const userDeptIdsRef = useRef([]);
  const rawDeptIds = cookies.departmentIds;
  userDeptIdsRef.current = Array.isArray(rawDeptIds)
    ? rawDeptIds.filter(Boolean)
    : rawDeptIds
    ? String(rawDeptIds).split(",").filter(Boolean)
    : [];

  const isInUserDept = useCallback(
    (deptId) => Boolean(deptId) && userDeptIdsRef.current.includes(deptId),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Ref untuk cek keberadaan chat tanpa dependency di callback
  const chatListRef = useRef([]);
  useEffect(() => {
    chatListRef.current = chatList;
  }, [chatList]);

  // Set chat_id yang sedang dalam proses fetch — cegah concurrent fetch untuk chat yang sama
  const pendingFetchRef = useRef(new Set());

  // Pesan baru dari WebSocket — diteruskan ke IgChatBody
  const [latestInbound, setLatestInbound] = useState(null);

  // Handler pesan masuk via WebSocket
  const handleNewMessage = useCallback(async (payload) => {
    // payload: { message_id, chat_id, text, from_username, from_id,
    //            direction, created_time, attachment_type, attachment_url, assigned_user_id }

    // Filter per role:
    // canSeeAll (Super Admin, System Admin, Admin, CS): terima semua — tidak difilter.
    // onlyOwn / SALES: hanya chat yang di-assign ke user ini.
    // HEAD (Access_Own_Dept): chat yang di-assign ke dirinya ATAU berada di deptnya.
    if (!canSeeAll) {
      if (onlyOwn) {
        if (payload.assigned_user_id !== currentUserId) return;
      } else {
        const isAssignedToMe = payload.assigned_user_id === currentUserId;
        if (!isAssignedToMe && !isInUserDept(payload.department_id)) return;
      }
    }

    setLatestInbound(payload);

    const existsInList = chatListRef.current.some((c) => c.conversation_id === payload.chat_id);
    const isFetching = pendingFetchRef.current.has(payload.chat_id);

    if (existsInList || isFetching) {
      // Chat sudah ada atau sedang di-fetch — cukup update preview & sort
      setChatList((prev) =>
        prev
          .map((chat) =>
            chat.conversation_id === payload.chat_id
              ? {
                  ...chat,
                  latest_message_text: payload.text || `[${payload.attachment_type || "attachment"}]`,
                  updated_at: payload.created_time || new Date().toISOString(),
                }
              : chat,
          )
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
      );
    } else {
      // Chat baru — fetch lalu tambah, dengan guard role tambahan sebagai double-check
      pendingFetchRef.current.add(payload.chat_id);
      const res = await igGetChatById(payload.chat_id);
      pendingFetchRef.current.delete(payload.chat_id);
      if (res?.data) {
        // Double-check setelah fetch — filter sesuai role (sama dengan guard di atas).
        if (!canSeeAll) {
          if (onlyOwn) {
            if (res.data.assigned_user_id !== currentUserId) return;
          } else {
            const isAssignedToMe = res.data.assigned_user_id === currentUserId;
            if (!isAssignedToMe && !isInUserDept(res.data.department_id)) return;
          }
        }
        setChatList((prev) => {
          if (prev.some((c) => c.conversation_id === res.data.conversation_id)) return prev;
          return [res.data, ...prev].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler assign via WebSocket — update assigned_user_id di chat list dan selected chat.
  // Untuk SALES role: jika chat di-assign ke user lain, hapus dari list.
  const handleChatAssigned = useCallback((data) => {
    // data: { type, chat_id, assigned_user_id, chat }
    if (!canSeeAll && data.assigned_user_id !== currentUserId) {
      const chatInList = chatListRef.current.find((c) => c.conversation_id === data.chat_id);
      const deptId = chatInList?.department_id ?? data.chat?.department_id;
      // HEAD di dept yang sama: tetap tampilkan untuk takeover (multi-user mode).
      // SALES: tidak ada pengecualian — langsung hapus dari list.
      if (!onlyOwn && isInUserDept(deptId)) {
        setChatList((prev) =>
          prev.map((c) => (c.conversation_id === data.chat_id ? { ...c, assigned_user_id: data.assigned_user_id } : c)),
        );
        return;
      }
      if (!chatInList) return;
      setChatList((prev) => prev.filter((c) => c.conversation_id !== data.chat_id));
      setSelectedChat((prev) => (prev?.conversation_id === data.chat_id ? null : prev));
      return;
    }
    setChatList((prev) =>
      prev.map((c) => (c.conversation_id === data.chat_id ? { ...c, assigned_user_id: data.assigned_user_id } : c)),
    );
    setSelectedChat((prev) =>
      prev?.conversation_id === data.chat_id ? { ...prev, assigned_user_id: data.assigned_user_id } : prev,
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler unassign via WebSocket — hapus assigned_user_id di chat list dan selected chat.
  // Untuk SALES role: chat yang di-unassign tidak boleh terlihat — hapus dari list.
  const handleChatUnassigned = useCallback((data) => {
    // data: { type, chat_id, chat }
    if (!canSeeAll) {
      const chatInList = chatListRef.current.find((c) => c.conversation_id === data.chat_id);
      const deptId = chatInList?.department_id ?? data.chat?.department_id;
      // HEAD di dept yang sama: tetap tampilkan walau unassigned — bisa diambil siapapun.
      // SALES: chat unassigned tidak boleh terlihat — hapus dari list.
      if (!onlyOwn && isInUserDept(deptId)) {
        setChatList((prev) =>
          prev.map((c) => (c.conversation_id === data.chat_id ? { ...c, assigned_user_id: null } : c)),
        );
        return;
      }
      if (!chatInList) return;
      setChatList((prev) => prev.filter((c) => c.conversation_id !== data.chat_id));
      setSelectedChat((prev) => (prev?.conversation_id === data.chat_id ? null : prev));
      return;
    }
    setChatList((prev) => prev.map((c) => (c.conversation_id === data.chat_id ? { ...c, assigned_user_id: null } : c)));
    setSelectedChat((prev) => (prev?.conversation_id === data.chat_id ? { ...prev, assigned_user_id: null } : prev));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler unread_update via WebSocket — update unread_count di chat list
  const handleUnreadUpdate = useCallback((data) => {
    // data: { type, chat_id, unread_count }
    setChatList((prev) =>
      prev.map((c) => (c.conversation_id === data.chat_id ? { ...c, unread_count: data.unread_count } : c)),
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler chat_read via WebSocket — reset unread_count ke 0
  const handleChatRead = useCallback((data) => {
    // data: { type, chat_id }
    setChatList((prev) =>
      prev.map((c) =>
        c.conversation_id === data.chat_id ? { ...c, unread_count: 0, last_read_at: new Date().toISOString() } : c,
      ),
    );
    setSelectedChat((prev) => (prev?.conversation_id === data.chat_id ? { ...prev, unread_count: 0 } : prev));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // System message baru dari WebSocket (assign/unassign) — diteruskan ke IgChatBody
  const [latestSystemMessage, setLatestSystemMessage] = useState(null);

  const handleSystemMessage = useCallback((data) => {
    // data: { type: "system_message", chat_id, message }
    setLatestSystemMessage(data);
  }, []);

  // Koneksi WebSocket real-time — kirim userId dan canSeeAll agar server bisa targeted delivery
  useIgWebSocket(
    handleNewMessage,
    handleChatAssigned,
    handleChatUnassigned,
    handleUnreadUpdate,
    handleChatRead,
    handleSystemMessage,
    currentUserId,
    canSeeAll,
  );

  const handleSelectChat = (chat) => {
    const shouldMarkRead = Boolean(chat?.conversation_id && chat.unread_count > 0);
    // Optimistic: crm-ig syncs the ticket to OPENED async on the same
    // condition (see ChatService.MarkRead) — reflect it here immediately
    // instead of waiting for a round trip / next full chat-list refetch.
    // Skip when already RESOLVED — crm-backend locks resolved tickets
    // against reopening (openExternalTicket), so flipping the badge here
    // would just be wrong until the async sync corrects it back.
    const ticketPatch = shouldMarkRead && chat.ticket_status !== "RESOLVED" ? { ticket_status: "OPENED" } : {};
    const nextChat = shouldMarkRead ? { ...chat, unread_count: 0, ...ticketPatch } : chat;

    setSelectedChat(nextChat);
    setSelectedId(chat?.conversation_id ?? null);

    if (shouldMarkRead) {
      setChatList((prev) =>
        prev.map((c) => (c.conversation_id === chat.conversation_id ? { ...c, unread_count: 0, ...ticketPatch } : c)),
      );
      igMarkChatRead(chat.conversation_id);
    }
  };

  return (
    <ContentAlt>
      <div className="nk-chat" style={{ width: "100%" }}>
        <IgChatAside onSelectChat={handleSelectChat} />
        <IgChatBody chat={selectedChat} newInboundMessage={latestInbound} newSystemMessage={latestSystemMessage} />
      </div>
    </ContentAlt>
  );
};

const Instagram = () => {
  return (
    <>
      <Head title="Instagram Platform" />
      <IgChatContextProvider>
        <InstagramInner />
      </IgChatContextProvider>
    </>
  );
};

export default Instagram;
